import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { AssetType, Media } from "./media";
import { MediaApi } from "./mediaApi";
import { OpenGraphData } from "./openGraphData";

const BIG_FILE_MIN = 5 * 1024 * 1024;

const CHUNK_SIZE = 5 * 1024 * 1024;
export enum UploadError {
	ALREADY_PENDING = "ALREADY_PENDING",
	CANCELED = "CANCELED",
	UNKNOWN = "UNKNOWN",
}

export class MediaService {
	assetStore = new SimpleStore((id) => this.mediaApi.fetch(id));
	myAssetsStore = new StorePresentation(this.assetStore, (page, query: string, type?: AssetType) =>
		this.mediaApi.list(page, query, type)
	);
	currentUploadState: "PENDING" | "CANCELED" | null = null;

	constructor(private readonly mediaApi: MediaApi) {}

	async delete(id: string, query?: string, type?: AssetType): Promise<void> {
		await this.mediaApi.delete(id);
		this.myAssetsStore.fetch(query ?? "", type);
	}

	async edit(id: string, displayName: string): Promise<void> {
		await this.mediaApi.edit(id, displayName);
		this.assetStore.update(id, (asset) => ({ ...asset, displayName }));
	}

	/** TODO if necessary use base64 image */
	// async fetchBase64(id: string): Promise<string> {
	// 	return this.mediaApi.fetchBase64(id);
	// }

	async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<Media> {
		if (this.currentUploadState === "PENDING") {
			throw Error(UploadError.ALREADY_PENDING);
		}
		this.currentUploadState = "PENDING";
		try {
			if (file.size < BIG_FILE_MIN) {
				const media = await this.mediaApi.uploadMedia(file, onProgress);
				this.currentUploadState = null;
				onProgress?.(1);
				this.myAssetsStore.fetch("");
				return media;
			}

			const { key, partUrls, uploadId } = await this.mediaApi.startUpload(file.size, CHUNK_SIZE);
			const etags = await this.uploadChunks(file, partUrls, onProgress);
			this.throwIfCanceled();
			const bigMedia = await this.mediaApi.finishUpload(key, uploadId, etags, file.type, file.name);
			this.currentUploadState = null;
			this.myAssetsStore.fetch("");
			return bigMedia;
		} catch (e) {
			this.currentUploadState = null;
			throw e;
		}
	}

	// async fetch(id: string): Promise<Media> {
	// 	return this.mediaApi.fetch(id)
	// }

	private async uploadChunks(
		file: File,
		partUrls: string[],
		onProgress?: (progress: number) => void
	): Promise<string[]> {
		const etags = [];
		for (const [i, url] of partUrls.entries()) {
			this.throwIfCanceled();
			const offset = i * CHUNK_SIZE;
			const chunk = file.slice(offset, offset + CHUNK_SIZE);
			const etag = await this.mediaApi.uploadChunk(chunk, url, (chunkProgress) => {
				onProgress?.(((chunkProgress + i) * 1) / partUrls.length);
			});
			etags.push(etag);
		}
		return etags;
	}

	private throwIfCanceled(): void {
		if (this.currentUploadState === "CANCELED") {
			throw Error(UploadError.CANCELED);
		}
	}

	cancelUpload(): void {
		if (this.currentUploadState) {
			this.currentUploadState = "CANCELED";
		}
	}

	fetchOpenGraphData(url: string): Promise<OpenGraphData> {
		return this.mediaApi.getOpenGraphData(url);
	}
}
